import { BoolNode, SearchAtom } from 'src/services/common.types';

// ----------------------------------------------------------------------

// Boolean operators the keyword search understands (item 23); they are not literal terms to highlight.
const OPERATORS = new Set(['AND', 'OR', 'NOT']);

// One character highlights half of every word on screen, so it is never worth marking.
const MIN_TERM_LENGTH = 2;

/**
 * Split a raw keyword search string (the keyword box, a column search, a `text_query` value, or the
 * `?q=` param) into the individual terms to highlight (spec item 18, JT1-1537). The backend splits
 * these on whitespace and requires every word, so each word is its own term. Quoted phrases are kept
 * whole; boolean operators and one-character noise are dropped. Returns an empty array when there is
 * nothing to highlight.
 */
export const parseHighlightTerms = (raw?: string | null): string[] => {
  if (!raw) return [];

  const tokens = raw.match(/"[^"]+"|\S+/g) ?? [];

  return tokens
    .map((token) => token.replace(/^"|"$/g, '').trim())
    .filter((term) => term.length >= MIN_TERM_LENGTH && !OPERATORS.has(term.toUpperCase()));
};

// ----------------------------------------------------------------------

/**
 * The parts of a candidate a search term can be highlighted in (JT1-1693). A term is only highlighted
 * where the search could have matched it: a Location criterion marks the location, not a job title
 * that happens to contain the same word.
 */
export type SearchHighlightField = 'name' | 'title' | 'email' | 'phone' | 'location' | 'skills' | 'company' | 'education' | 'summary';

/**
 * Search terms to highlight. `any` holds terms the search looked for everywhere (the keyword box and
 * Text Search); `fields` holds terms that were searched in one field only.
 */
export interface SearchHighlights {
  any: string[];
  fields: Partial<Record<SearchHighlightField, string[]>>;
}

export const EMPTY_HIGHLIGHTS: SearchHighlights = { any: [], fields: {} };

/** Case-insensitive de-duplication that keeps the first spelling seen. */
const uniqueTerms = (terms: string[]): string[] => {
  const seen = new Map<string, string>();
  terms.forEach((term) => {
    const key = term.toLowerCase();
    if (!seen.has(key)) seen.set(key, term);
  });
  return Array.from(seen.values());
};

/** Terms searched for in a field: the everywhere terms plus that field's own. */
export const highlightTermsFor = (highlights: SearchHighlights, field: SearchHighlightField): string[] =>
  uniqueTerms([...highlights.any, ...(highlights.fields[field] ?? [])]);

/** Every term in the search, for surfaces that show many fields at once (the record panel, the resume). */
export const allHighlightTerms = (highlights: SearchHighlights): string[] =>
  uniqueTerms([...highlights.any, ...Object.values(highlights.fields).flat()]);

/** Combine the highlights of searches that apply together (e.g. the keyword box plus column searches). */
export const mergeHighlights = (...parts: SearchHighlights[]): SearchHighlights => {
  const fields: SearchHighlights['fields'] = {};
  parts.forEach((part) => {
    (Object.entries(part.fields) as [SearchHighlightField, string[]][]).forEach(([field, terms]) => {
      fields[field] = uniqueTerms([...(fields[field] ?? []), ...terms]);
    });
  });
  return { any: uniqueTerms(parts.flatMap((part) => part.any)), fields };
};

/** The candidate list keyword box. The backend scans every text field for each word. */
export const keywordHighlights = (raw?: string | null): SearchHighlights => ({ any: parseHighlightTerms(raw), fields: {} });

// Per-column search keys (candidate-columns.tsx) to the field they filter.
const COLUMN_SEARCH_FIELDS: Record<string, SearchHighlightField> = {
  name: 'name',
  position_title: 'title',
  email: 'email',
  location: 'location',
};

/** The per-column search boxes. Each matches its own column only, word by word. */
export const columnSearchHighlights = (columnSearch?: Record<string, string>): SearchHighlights => {
  const fields: SearchHighlights['fields'] = {};
  Object.entries(columnSearch ?? {}).forEach(([key, value]) => {
    const field = COLUMN_SEARCH_FIELDS[key];
    const terms = parseHighlightTerms(value);
    if (field && terms.length) fields[field] = uniqueTerms([...(fields[field] ?? []), ...terms]);
  });
  return { any: [], fields };
};

/**
 * Advanced-search fields whose values are free text a candidate record can contain, and where each is
 * shown. Labels (stored as ids), enums (availability, work rights, education level) and numbers (salary,
 * notice period) never appear as text, so they are left out.
 */
const DSL_FIELDS: Record<string, SearchHighlightField> = {
  role_title: 'title',
  company: 'company',
  skills: 'skills',
  location: 'location',
  education_institution: 'education',
  executive_summary: 'summary',
};

// Operators the backend compiles word by word (every word must appear), rather than as a whole value.
const WORD_OPERATORS = new Set(['match_phrase']);

/**
 * Highlights for an Advanced Search DSL: the keyword (folded in as a `text_query` atom) and the
 * criteria rows. Terms are split the same way the backend compiles them (JT1-1693):
 *
 * - `text_query` searches every field, one word at a time, so its words go to `any`.
 * - `match_phrase` requires every word, so a phrase criterion is highlighted word by word.
 * - `contains_any` / `contains_all` / `eq` match each value whole, so "Project Management" as a skill
 *   stays one phrase rather than marking every stray "management".
 *
 * Terms under a NOT are skipped, because an excluded term is the opposite of a match.
 */
export const dslHighlights = (dsl?: BoolNode | null): SearchHighlights => {
  const any: string[] = [];
  const fields: SearchHighlights['fields'] = {};

  const visit = (node: BoolNode | SearchAtom) => {
    if ('op' in node) {
      if (node.op !== 'NOT') (node.children ?? []).forEach(visit);
      return;
    }

    const isText = node.field === 'text_query';
    const field = DSL_FIELDS[node.field];
    if (!isText && !field) return;

    const values = (Array.isArray(node.value) ? node.value : [node.value])
      .filter((value): value is string => typeof value === 'string');

    const terms = isText || WORD_OPERATORS.has(node.operator)
      ? values.flatMap(parseHighlightTerms)
      : values.map((value) => value.trim()).filter((value) => value.length >= MIN_TERM_LENGTH);

    if (isText) any.push(...terms);
    else fields[field] = [...(fields[field] ?? []), ...terms];
  };

  if (dsl) visit(dsl);

  return mergeHighlights({ any, fields });
};

/**
 * Write terms into the `?q=` param so an opened record can highlight them. A multi-word phrase is
 * quoted so `parseHighlightTerms` reads it back as one term instead of splitting it into words.
 */
export const serializeHighlightTerms = (terms: string[]): string =>
  uniqueTerms(terms)
    .map((term) => term.replace(/"/g, '').trim())
    .filter(Boolean)
    .map((term) => (/\s/.test(term) ? `"${term}"` : term))
    .join(' ');

/**
 * Build the `?q=` highlight string for a record opened from Advanced Search, so its resume and header
 * highlight every term the recruiter searched on.
 */
export const highlightQueryFromDsl = (dsl?: BoolNode | null): string => serializeHighlightTerms(allHighlightTerms(dslHighlights(dsl)));
